import React, { useState } from 'react';
import { useNavigation } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Row,
  Col,
  Divider,
  message,
  Modal,
  Descriptions,
  Popconfirm,
} from 'antd';
import { PlusOutlined, TrophyOutlined, EyeOutlined, StopOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { Tournament } from '@/types';
import type { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Helper to map tier number to name
const tierMap: Record<number, string> = {
  1: 'bronze',
  2: 'silver',
  3: 'gold',
  4: 'platinum',
  5: 'diamond',
};

const tierColors: Record<string, string> = {
  bronze: 'orange',
  silver: 'default',
  gold: 'gold',
  platinum: 'blue',
  diamond: 'purple',
};

const statusColors: Record<string, string> = {
  upcoming: 'blue',
  registration: 'cyan',
  in_progress: 'green',
  completed: 'default',
  cancelled: 'red',
};

// Tournament List
export const TournamentList: React.FC = () => {
  const { create } = useNavigation();
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const { tableProps, tableQueryResult } = useTable<Tournament>({
    resource: 'tournaments',
    syncWithLocation: true,
    pagination: { pageSize: 20 },
  });

  const handleView = (record: Tournament) => {
    setSelectedTournament(record);
    setViewModalVisible(true);
  };

  const handleCancel = async (record: Tournament) => {
    if (!cancelReason.trim()) {
      message.error('Please provide a reason for cancellation');
      return;
    }

    setCancelling(true);
    try {
      const response = await adminApi.cancelTournament(record.id, cancelReason);
      if (response.data.success) {
        message.success(response.data.message || 'Tournament cancelled successfully');
        setCancelReason('');
        tableQueryResult.refetch();
      } else {
        message.error(response.data.message || 'Failed to cancel tournament');
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to cancel tournament';
      message.error(errorMessage || 'Failed to cancel tournament');
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space>
          <TrophyOutlined />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag>{type?.toUpperCase()}</Tag>,
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      render: (tier: number | string) => {
        const tierName = typeof tier === 'number' ? tierMap[tier] : tier;
        return <Tag color={tierColors[tierName]}>{tierName?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Participants',
      key: 'participants',
      render: (_: unknown, record: Tournament) =>
        `${record.currentParticipants || 0}/${record.maxParticipants}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{status?.replace(/_/g, ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Starts At',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Tournament) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          {(record.status === 'upcoming' || record.status === 'registration') && (
            <Popconfirm
              title="Cancel Tournament"
              description={
                <div style={{ width: 300 }}>
                  <Text>Are you sure you want to cancel this tournament?</Text>
                  <Input.TextArea
                    placeholder="Enter cancellation reason..."
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={3}
                    style={{ marginTop: 8 }}
                  />
                </div>
              }
              onConfirm={() => handleCancel(record)}
              okText="Cancel Tournament"
              cancelText="No"
              okButtonProps={{ danger: true, loading: cancelling }}
            >
              <Button type="link" size="small" danger icon={<StopOutlined />}>
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Tournaments
            </Title>
            <Text type="secondary">Create and manage tournaments</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => create('tournaments')}>
            Create Tournament
          </Button>
        </Space>
      </div>

      <Card>
        <Table {...tableProps} columns={columns} rowKey="id" />
      </Card>

      {/* View Tournament Modal */}
      <Modal
        title={
          <Space>
            <TrophyOutlined />
            {selectedTournament?.name}
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedTournament(null);
        }}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedTournament && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="ID" span={2}>
              <Text copyable>{selectedTournament.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Name">{selectedTournament.name}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag>{selectedTournament.type?.toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tier">
              <Tag
                color={
                  tierColors[
                    typeof selectedTournament.tier === 'number'
                      ? tierMap[selectedTournament.tier]
                      : selectedTournament.tier
                  ]
                }
              >
                {(typeof selectedTournament.tier === 'number'
                  ? tierMap[selectedTournament.tier]
                  : selectedTournament.tier
                )?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColors[selectedTournament.status]}>
                {selectedTournament.status?.replace(/_/g, ' ').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Game Mode">{selectedTournament.gameMode}</Descriptions.Item>
            <Descriptions.Item label="Overs">{selectedTournament.overs}</Descriptions.Item>
            <Descriptions.Item label="Participants">
              {selectedTournament.currentParticipants} / {selectedTournament.maxParticipants}
            </Descriptions.Item>
            <Descriptions.Item label="Entry Fee">
              {selectedTournament.entryFee > 0 ? `${selectedTournament.entryFee} coins` : 'Free'}
            </Descriptions.Item>
            <Descriptions.Item label="Min Division Tier">
              {selectedTournament.minDivisionTier || 'None'}
            </Descriptions.Item>
            <Descriptions.Item label="Registration Start">
              {selectedTournament.registrationStart
                ? new Date(selectedTournament.registrationStart).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Registration End">
              {selectedTournament.registrationEnd
                ? new Date(selectedTournament.registrationEnd).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Start Time">
              {selectedTournament.startTime
                ? new Date(selectedTournament.startTime).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedTournament.createdAt
                ? new Date(selectedTournament.createdAt).toLocaleString()
                : '-'}
            </Descriptions.Item>
            {selectedTournament.description && (
              <Descriptions.Item label="Description" span={2}>
                {selectedTournament.description}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

// Tournament Create
export const TournamentCreate: React.FC = () => {
  const { list } = useNavigation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  interface TournamentFormValues {
    name: string;
    description?: string;
    type: string;
    tier: string; // UI uses string, will convert to number
    gameMode: string;
    overs: number;
    maxParticipants: number;
    minDivisionTier?: number;
    entryFee?: number;
    registrationStart: Dayjs;
    registrationEnd: Dayjs;
    startTime: Dayjs;
  }

  const handleSubmit = async (values: TournamentFormValues) => {
    setLoading(true);
    try {
      // Convert tier string to number (1-5)
      const tierMap: Record<string, number> = {
        bronze: 1,
        silver: 2,
        gold: 3,
        platinum: 4,
        diamond: 5,
      };

      await adminApi.createTournament({
        name: values.name,
        description: values.description,
        type: values.type,
        tier: tierMap[values.tier] || 1,
        gameMode: values.gameMode,
        overs: values.overs,
        maxParticipants: values.maxParticipants,
        minDivisionTier: values.minDivisionTier,
        entryFee: values.entryFee || 0,
        registrationStart: values.registrationStart.toISOString(),
        registrationEnd: values.registrationEnd.toISOString(),
        startTime: values.startTime.toISOString(),
      });
      message.success('Tournament created successfully');
      list('tournaments');
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to create tournament';
      message.error(errorMessage || 'Failed to create tournament');
      console.error('Create tournament error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Title level={3}>Create Tournament</Title>
        <Text type="secondary">Set up a new competitive tournament</Text>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: 'knockout',
            tier: 'bronze',
            gameMode: 'quick_play',
            overs: 3,
            maxParticipants: 32,
            minDivisionTier: 0,
            entryFee: 0,
          }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <div className="form-section">
                <div className="form-section-title">Basic Info</div>

                <Form.Item
                  name="name"
                  label="Tournament Name"
                  rules={[{ required: true, message: 'Please enter a name' }]}
                >
                  <Input placeholder="Weekend Showdown #1" />
                </Form.Item>

                <Form.Item name="description" label="Description">
                  <TextArea rows={3} placeholder="Tournament description..." />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                      <Select>
                        <Select.Option value="knockout">Knockout</Select.Option>
                        <Select.Option value="league">League</Select.Option>
                        <Select.Option value="swiss">Swiss</Select.Option>
                        <Select.Option value="round_robin">Round Robin</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="tier" label="Tier" rules={[{ required: true }]}>
                      <Select>
                        <Select.Option value="bronze">Bronze (Free)</Select.Option>
                        <Select.Option value="silver">Silver</Select.Option>
                        <Select.Option value="gold">Gold</Select.Option>
                        <Select.Option value="platinum">Platinum</Select.Option>
                        <Select.Option value="diamond">Diamond</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="maxParticipants"
                  label="Max Participants"
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Select.Option value={8}>8 Players</Select.Option>
                    <Select.Option value={16}>16 Players</Select.Option>
                    <Select.Option value={32}>32 Players</Select.Option>
                    <Select.Option value={64}>64 Players</Select.Option>
                    <Select.Option value={128}>128 Players</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <div className="form-section">
                <div className="form-section-title">Game Settings</div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="gameMode" label="Game Mode" rules={[{ required: true }]}>
                      <Select>
                        <Select.Option value="quick_play">Quick Play (2 Overs)</Select.Option>
                        <Select.Option value="t20">T20 (20 Overs)</Select.Option>
                        <Select.Option value="odi">ODI (50 Overs)</Select.Option>
                        <Select.Option value="custom_match">Custom Match</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="overs" label="Overs" rules={[{ required: true }]}>
                      <InputNumber min={3} max={90} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>

            <Col xs={24} lg={12}>
              <div className="form-section">
                <div className="form-section-title">Entry Requirements</div>

                <Form.Item name="minDivisionTier" label="Min Division Tier (0 = No restriction)">
                  <InputNumber
                    min={0}
                    max={21}
                    style={{ width: '100%' }}
                    placeholder="0 for no restriction"
                  />
                </Form.Item>

                <Form.Item name="entryFee" label="Entry Fee (Coins)" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0 for free entry" />
                </Form.Item>
              </div>

              <div className="form-section">
                <div className="form-section-title">Schedule</div>

                <Form.Item
                  name="registrationStart"
                  label="Registration Opens"
                  rules={[{ required: true, message: 'Please select registration start time' }]}
                >
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="registrationEnd"
                  label="Registration Closes"
                  rules={[{ required: true, message: 'Please select registration end time' }]}
                >
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="startTime"
                  label="Tournament Starts At"
                  rules={[{ required: true, message: 'Please select tournament start time' }]}
                >
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              </div>
            </Col>
          </Row>

          <Divider />

          <div className="form-section">
            <div className="form-section-title">Rewards (Optional)</div>
            <Text type="secondary">
              Leave empty to use default rewards based on tier. Custom rewards can be configured
              after creation.
            </Text>
          </div>

          <Divider />

          <Space>
            <Button onClick={() => list('tournaments')}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Tournament
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default { TournamentList, TournamentCreate };
